import React, { useState, useEffect } from 'react';
import { StandardURL } from '../services/constants';

// ─── Inline Styles for Dark Theme ──────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');

  .admin-wrapper {
    background: #0a0a0a;
    color: #f0f0f0;
    min-height: 100vh;
    padding: 3rem 2rem;
    font-family: 'DM Sans', sans-serif;
  }
  .admin-container {
    max-width: 800px; /* Slightly narrower for categories */
    margin: 0 auto;
  }
  .admin-card {
    background: #111111;
    border: 1px solid #2a2a2a;
    border-radius: 8px;
    padding: 2.5rem;
    margin-bottom: 2rem;
  }
  .admin-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2.5rem;
    letter-spacing: 0.06em;
    margin-bottom: 1.5rem;
  }
  .admin-title span {
    color: #e8ff3c; /* Yellow accent */
  }
  .msg-box {
    padding: 1rem;
    background: rgba(232, 255, 60, 0.1);
    color: #e8ff3c;
    border: 1px solid rgba(232, 255, 60, 0.2);
    border-radius: 6px;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
  }
  
  /* Form Styles */
  .admin-form label {
    display: block;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #666;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  .admin-form input {
    width: 100%;
    background: #1a1a1a;
    border: 1px solid #2a2a2a;
    color: #f0f0f0;
    border-radius: 6px;
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    font-family: 'DM Sans', sans-serif;
    outline: none;
    transition: border-color 0.2s;
  }
  .admin-form input:focus {
    border-color: #e8ff3c;
  }
  .form-group {
    margin-bottom: 1.5rem;
  }
  .btn-submit {
    background: #e8ff3c;
    color: #000;
    border: none;
    border-radius: 4px;
    padding: 0.85rem 2rem;
    font-family: 'DM Sans', sans-serif;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: opacity 0.2s;
    width: 100%;
  }
  .btn-submit:hover:not(:disabled) {
    opacity: 0.85;
  }
  .btn-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Table Styles */
  .admin-table-wrap {
    overflow-x: auto;
  }
  .admin-table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
  }
  .admin-table th {
    padding: 1rem;
    border-bottom: 1px solid #2a2a2a;
    color: #666;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .admin-table td {
    padding: 1rem;
    border-bottom: 1px solid #1a1a1a;
    vertical-align: middle;
    font-size: 0.9rem;
  }
  .admin-table tr:hover {
    background: rgba(255, 255, 255, 0.02);
  }
  .accent-text {
    color: #e8ff3c;
    font-family: monospace;
  }
  .btn-delete {
    background: transparent;
    color: #ff4545;
    border: 1px solid rgba(255, 69, 69, 0.3);
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 600;
    transition: all 0.2s;
  }
  .btn-delete:hover {
    background: rgba(255, 69, 69, 0.1);
    border-color: #ff4545;
  }
`;

const CategoryManager = () => {
  // Get token and userId from local storage
  const token = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  const userData = userDataString ? JSON.parse(userDataString) : {};
  const userId = userData.unique || ""; // Matches the logic from landing.js

  // State
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${StandardURL}/iMusic/user/category/listadmin`);
      const data = await response.json();
      
      // Strictly enforce that we are saving an array
      const categoryArray = Array.isArray(data?.response) 
        ? data.response 
        : (Array.isArray(data) ? data : []);
        
      setCategories(categoryArray);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]); 
    }
  };

  // Handle Adding a new category
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    if (!userId) {
      setMessage('Error: User ID not found. Please log in again.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${StandardURL}/iMusic/user/category/add/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', // We send JSON here, not FormData
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ category: newCategory.trim() }),
      });

      if (response.ok) {
        setMessage('Category added successfully! ✅');
        setNewCategory(''); // Clear input
        fetchCategories();  // Refresh the list
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage(errorData.error || errorData.message || 'Failed to add category.');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      setMessage('An error occurred while adding the category.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Deleting a category
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const response = await fetch(`${StandardURL}/iMusic/user/category/delete/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setMessage('Category deleted successfully. 🗑️');
        fetchCategories(); // Refresh list
      } else {
        setMessage('Failed to delete category.');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      setMessage('An error occurred while deleting.');
    }
  };

  return (
    <div className="admin-wrapper">
      <style>{styles}</style>
      
      <div className="admin-container">
        
        {message && <div className="msg-box">{message}</div>}

        {/* --- ADD CATEGORY FORM --- */}
        <div className="admin-card">
          <h2 className="admin-title">ADD <span>CATEGORY</span></h2>
          
          <form onSubmit={handleAddCategory} className="admin-form">
            <div className="form-group">
              <label>Category Name</label>
              <input 
                type="text" 
                placeholder="e.g. Afrobeats, Drill, R&B..."
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)} 
                required 
              />
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Adding...' : 'Create Category'}
            </button>
          </form>
        </div>

        {/* --- CATEGORY LIST --- */}
        <div className="admin-card">
          <h2 className="admin-title">MANAGE <span>CATEGORIES</span></h2>
          
          {categories.length === 0 ? (
            <p style={{ color: '#666' }}>No categories found.</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Category Name</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr key={cat.id || cat.category}>
                      <td className="accent-text">#{cat.id}</td>
                      <td style={{ fontWeight: 500 }}>{cat.category}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button 
                          className="btn-delete"
                          onClick={() => handleDelete(cat.id)}
                        >
                          ✕ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default CategoryManager;